import type { ReactNode } from "react";

/**
 * Shared atoms for the "Field Instrument" landing aesthetic.
 *
 * The whole page leans on a small set of motifs: bracketed section
 * numbers, mono technical labels, L-shaped corner brackets, and
 * coordinate-style annotations. Keeping them here lets sections stay
 * focused on their actual content.
 */

export function SectionLabel({
  index,
  title,
  kicker,
}: {
  index: string;
  title: string;
  kicker?: string;
}) {
  return (
    <div className="flex items-baseline gap-4 mb-10 md:mb-14">
      <div className="label-mono shrink-0">
        <span className="bracket">{index}</span>
      </div>
      <div className="flex-1 h-px bg-[color:var(--color-app-edge-2)]" />
      <div className="text-right">
        <div className="label-mono text-[color:var(--color-app-text)]/70">{title}</div>
        {kicker && <div className="coord-mono opacity-50 mt-0.5">{kicker}</div>}
      </div>
    </div>
  );
}

export function CornerCard({
  children,
  highlighted,
  tint,
  className = "",
}: {
  children: ReactNode;
  highlighted?: boolean;
  /** Optional bracket-colour override for variety across the page. */
  tint?: "amber" | "violet" | "water" | "coral" | "saffron" | "leaf";
  className?: string;
}) {
  const tintClass = tint ? `corners-${tint}` : "";
  return (
    <div
      className={[
        "relative",
        highlighted ? "corners corners-strong" : "corners",
        tintClass,
        "border border-[color:var(--color-app-edge)]",
        "bg-[color:var(--color-app-card)]",
        "backdrop-blur-sm",
        className,
      ].join(" ")}
    >
      <span className="corner-tr" />
      <span className="corner-bl" />
      {children}
    </div>
  );
}

/** Small status pip — colored dot + caps label. */
export function StatusPip({
  color = "emerald",
  children,
  pulse,
}: {
  color?: "emerald" | "amber" | "water" | "rust" | "mute" | "coral" | "saffron" | "violet";
  children: ReactNode;
  pulse?: boolean;
}) {
  const dot: Record<string, string> = {
    emerald: "bg-[color:var(--color-leaf)]",
    amber:   "bg-[color:var(--color-amber)]",
    water:   "bg-[color:var(--color-water)]",
    rust:    "bg-[color:var(--color-rust)]",
    mute:    "bg-[color:var(--color-app-mute)]",
    coral:   "bg-[color:var(--color-coral)]",
    saffron: "bg-[color:var(--color-saffron)]",
    violet:  "bg-[color:var(--color-violet)]",
  };
  return (
    <span className="inline-flex items-center gap-1.5 label-mono">
      <span className={`relative w-1.5 h-1.5 rounded-full ${dot[color]} ${pulse ? "pulse-dot" : ""}`} />
      {children}
    </span>
  );
}

export function Kicker({ children }: { children: ReactNode }) {
  return <div className="label-mono mb-3">{children}</div>;
}

/** Mono technical readout — uses tabular numerals for stability. */
export function Readout({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  return (
    <div>
      <div className="label-mono opacity-60">{label}</div>
      <div className="font-mono text-sm mt-1 num">{value}</div>
    </div>
  );
}
